import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Multi-image uploader. Accepts URLs and uploaded files.
 * value = string[] of URLs (may include /api/media/... or external URLs)
 * onChange(urls) — called with new array
 */
export default function ImageUploader({ value = [], onChange, kind = "products" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/admin/upload?kind=${kind}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r.data.url);
      } catch (e) {
        toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
      }
    }
    if (uploaded.length) {
      onChange([...(Array.isArray(value) ? value : []), ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length>1?"s":""} uploaded`);
    }
    setUploading(false);
  };

  const remove = (idx) => {
    const next = [...value]; next.splice(idx, 1); onChange(next);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          uploadFiles(Array.from(e.dataTransfer.files || []));
        }}
        onClick={() => inputRef.current?.click()}
        data-testid="image-uploader-drop"
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-stone-300 hover:border-stone-400"
        }`}
      >
        <input ref={inputRef} type="file" accept="image/*" multiple hidden data-testid="image-uploader-input"
               onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-stone-600">
            <Loader2 className="w-4 h-4 animate-spin"/> Uploading…
          </div>
        ) : (
          <div className="text-sm text-stone-500">
            <Upload className="w-5 h-5 mx-auto mb-2"/>
            <div>Drop images here or <span className="text-[#3B2F2F] underline">click to browse</span></div>
            <div className="text-xs mt-1">PNG, JPG, WebP up to 8MB each</div>
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-3" data-testid="image-uploader-list">
          {value.map((url, i) => (
            <div key={i} className="relative group aspect-square">
              <img src={url} alt="" className="w-full h-full object-cover rounded-md border border-stone-200"/>
              <button type="button" onClick={() => remove(i)} data-testid={`remove-image-${i}`}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3 text-red-600"/>
              </button>
              {i === 0 && (
                <div className="absolute bottom-1 left-1 bg-[#D4AF37] text-[#3B2F2F] text-[10px] font-semibold px-1.5 py-0.5 rounded">Cover</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
